import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../lib/api';

/**
 * Navigation component for detail views (Startup, Investor, etc.)
 * Shows Previous/Next buttons to navigate between entities
 */
const DetailViewNavigation = ({ type, currentId }) => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [allItems, setAllItems] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadItems();
    }, [type]);

    useEffect(() => {
        if (allItems.length > 0 && currentId) {
            const index = allItems.findIndex(item => item.id === parseInt(currentId));
            setCurrentIndex(index);
        }
    }, [allItems, currentId]);

    const loadItems = async () => {
        setLoading(true);
        try {
            let items = [];
            if (type === 'startup') {
                items = await adminAPI.getStartups();
            } else if (type === 'investor') {
                items = await adminAPI.getInvestors();
            }
            setAllItems(items || []);
        } catch (error) {
            console.error('Failed to load items for navigation:', error);
            setAllItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            const prevItem = allItems[currentIndex - 1];
            navigate(`/${tenantId}/${type}s/${prevItem.id}?tab=overview`);
        }
    };

    const handleNext = () => {
        if (currentIndex < allItems.length - 1) {
            const nextItem = allItems[currentIndex + 1];
            navigate(`/${tenantId}/${type}s/${nextItem.id}?tab=overview`);
        }
    };

    if (loading || allItems.length === 0 || currentIndex === -1) {
        return null;
    }

    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < allItems.length - 1;

    return (
        <div className="flex items-center space-x-2">
            {/* Navigation Info */}
            <div className="text-xs text-gray-500 mr-2">
                {currentIndex + 1} of {allItems.length}
            </div>

            {/* Previous Button */}
            <button
                onClick={handlePrevious}
                disabled={!hasPrevious}
                className={`p-2 rounded-lg border transition-all ${hasPrevious
                        ? 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                        : 'border-gray-200 text-gray-300 cursor-not-allowed'
                    }`}
                title={hasPrevious ? `Previous ${type}` : 'No previous'}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Next Button */}
            <button
                onClick={handleNext}
                disabled={!hasNext}
                className={`p-2 rounded-lg border transition-all ${hasNext
                        ? 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                        : 'border-gray-200 text-gray-300 cursor-not-allowed'
                    }`}
                title={hasNext ? `Next ${type}` : 'No next'}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>
        </div>
    );
};

export default DetailViewNavigation;
